import type { ShapeData } from '@/lib/shapes/types/shape-data'
import type { TextBoxData } from '@/lib/text/types/text-box-data'
import { isGroupShape } from '@/lib/groups/group-kind'

/** Collect selected shapes plus every descendant under selected group shells (SC-G-08). */
export function expandShapeSnapshotsForClipboard(
  selectedShapeIds: string[],
  allShapes: ShapeData[]
): ShapeData[] {
  if (selectedShapeIds.length === 0) return []

  const byId = new Map(allShapes.map((shape) => [shape.id, shape]))
  const included = new Set<string>()

  const visit = (id: string) => {
    if (included.has(id)) return
    const shape = byId.get(id)

    if (!shape) return

    included.add(id)

    if (!isGroupShape(shape)) return

    for (const child of allShapes) {
      if (child.parentGroupId === id) {
        visit(child.id)
      }
    }
  }

  for (const id of selectedShapeIds) {
    visit(id)
  }

  return allShapes.filter((shape) => included.has(shape.id))
}

/** Text explicitly selected or parented under a shape included in the clipboard (SC-G-08). */
export function expandTextSnapshotsForClipboard(params: {
  shapeSnapshots: ShapeData[]
  selectedTextIds: string[]
  allTextBoxes: TextBoxData[]
  allShapes: ShapeData[]
}): TextBoxData[] {
  const { shapeSnapshots, selectedTextIds, allTextBoxes, allShapes } = params
  const copiedShapeIds = new Set(shapeSnapshots.map((shape) => shape.id))
  const shapesById = new Map(allShapes.map((shape) => [shape.id, shape]))
  const included = new Set<string>()

  const includeIfUnderCopiedShapes = (box: TextBoxData) => {
    let groupId = box.parentGroupId

    while (groupId) {
      if (copiedShapeIds.has(groupId)) {
        included.add(box.id)
        return
      }

      groupId = shapesById.get(groupId)?.parentGroupId
    }
  }

  for (const id of selectedTextIds) {
    const box = allTextBoxes.find((candidate) => candidate.id === id)

    if (box) included.add(box.id)
  }

  for (const box of allTextBoxes) {
    if (included.has(box.id)) continue
    includeIfUnderCopiedShapes(box)
  }

  return allTextBoxes.filter((box) => included.has(box.id))
}

/** Remap `parentGroupId` when pasting; parents outside the copy become root-level. */
export function remapClipboardParentGroupId(
  parentGroupId: string | undefined,
  idMap: ReadonlyMap<string, string>
): string | undefined {
  if (!parentGroupId) return undefined

  return idMap.get(parentGroupId)
}

/**
 * Nesting depth of `snapshot` within `snapshotsById` via `parentGroupId` chains (0 = top-level,
 * or its parent isn't part of the same copied/pasted set).
 *
 * Paste/duplicate must create each pasted group only after every descendant it needs (shapes and
 * text) already exists — see `handleShapeCreated` in use-home-shape-editing.ts, which persists a newly created
 * group by reading the current shape/text manager state at the moment the group itself is
 * created. Sort snapshots by *descending* depth (deepest first) before creating them so an outer
 * group is never created before an inner group (or leaf) it contains.
 */
export function shapeSnapshotNestingDepth(
  snapshot: { parentGroupId?: string },
  snapshotsById: ReadonlyMap<string, { parentGroupId?: string }>
): number {
  let depth = 0
  let parentId = snapshot.parentGroupId

  while (parentId && snapshotsById.has(parentId)) {
    depth++
    parentId = snapshotsById.get(parentId)?.parentGroupId
  }

  return depth
}
